import React from "react";
import { motion } from "framer-motion";
import {
  Beaker,
  Layers,
  Settings,
  ShieldCheck,
  FlaskConical,
  Microscope,
} from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Intermediates() {
  return (
    <div className="w-full">
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/intermediates-banner.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Pharma Intermediates
        </h1>
      </div>

      {/* Content Section with Motion */}
      <motion.div
        className="max-w-6xl mx-auto px-4 py-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {/* Intro */}
        <motion.p className="text-lg text-gray-700 mb-10" variants={fadeInUp}>
          At <span className="font-semibold text-[#0d47a1]">MAC LABS LTD</span>, we
          manufacture a wide range of high-purity pharmaceutical intermediates that
          act as essential precursors in the synthesis of Active Pharmaceutical
          Ingredients. Our intermediates are developed with a strong focus on process
          safety, consistency, and efficiency, ensuring seamless integration into
          diverse drug development and manufacturing pathways.
        </motion.p>

        {/* Strengths */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Our Strengths
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Animated Cards */}
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Beaker className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              High Purity & Reliability
            </h3>
            <p className="text-gray-600">
              Produced under controlled conditions to ensure maximum consistency and
              reproducibility.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Layers className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Scalable Capabilities
            </h3>
            <p className="text-gray-600">
              From lab-scale development to large-scale commercial production.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Settings className="w-8 h-8 text-orange-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Customized Solutions
            </h3>
            <p className="text-gray-600">
              Tailored intermediates designed in collaboration with partners.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ShieldCheck className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Robust Quality Control
            </h3>
            <p className="text-gray-600">
              Stringent testing ensures compliance with global standards.
            </p>
          </motion.div>
        </div>

        {/* Applications */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Applications
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <FlaskConical className="w-8 h-8 text-red-500 mb-3" />
            <p className="text-gray-700">
              Building blocks for a broad spectrum of therapeutic categories.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Microscope className="w-8 h-8 text-indigo-600 mb-3" />
            <p className="text-gray-700">
              Enabling cost-effective and efficient API synthesis.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Beaker className="w-8 h-8 text-teal-600 mb-3" />
            <p className="text-gray-700">
              Supporting innovative research and development pipelines.
            </p>
          </motion.div>
        </div>

        {/* Why Choose Us */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Why Choose Us?
        </motion.h2>
        <motion.ul
          className="list-disc list-inside text-gray-600 space-y-2 mb-10"
          variants={fadeInUp}
        >
          <li>Proven expertise in complex organic synthesis.</li>
          <li>Commitment to delivering safe and sustainable solutions.</li>
          <li>Flexible production tailored to partner requirements.</li>
          <li>Trusted global partner for pharma innovation.</li>
        </motion.ul>

        {/* Closing */}
        <motion.p className="text-gray-700 font-medium" variants={fadeInUp}>
          With <span className="font-semibold">MAC LABS LTD</span>, you gain access to
          intermediates that not only strengthen your API pipeline but also drive
          innovation, efficiency, and long-term value.
        </motion.p>
      </motion.div>
    </div>
  );
}
